/* ───────────────────────────────────────────────────────────────────────
   Demo Speed — global multiplier applied to every scenario's setTimeout.

   Default: 1× (zero behavior change vs baseline).
   Options: 1×, 1.5×, 2× (toggle from cheat sheet).
   Persists in localStorage('implify-demo-speed').

   Usage in any auto-play orchestrator:
     import { scaleDelay } from '../hooks/demoSpeed';
     setTimeout(cb, scaleDelay(800));   // 800ms at 1×, 400ms at 2×
   ─────────────────────────────────────────────────────────────────────── */

const STORAGE_KEY = 'implify-demo-speed';
const VALID_SPEEDS = [1, 1.5, 2];
const MIN_DELAY_MS = 80; // floor — prevents zero-ms race conditions

let currentSpeed = 1;
const subscribers = new Set();

// Read persisted value on module load
try {
  const stored = parseFloat(localStorage.getItem(STORAGE_KEY));
  if (VALID_SPEEDS.includes(stored)) currentSpeed = stored;
} catch { /* noop */ }

export const scaleDelay = (ms) => {
  if (typeof ms !== 'number' || isNaN(ms) || ms <= 0) return ms;
  const scaled = ms / currentSpeed;
  return Math.max(MIN_DELAY_MS, Math.round(scaled));
};

export const getDemoSpeed = () => currentSpeed;

export const setDemoSpeed = (speed) => {
  if (!VALID_SPEEDS.includes(speed)) return;
  currentSpeed = speed;
  try { localStorage.setItem(STORAGE_KEY, String(speed)); } catch { /* noop */ }
  subscribers.forEach((cb) => { try { cb(speed); } catch { /* noop */ } });
};

export const subscribeDemoSpeed = (cb) => {
  subscribers.add(cb);
  return () => subscribers.delete(cb);
};

export const DEMO_SPEED_OPTIONS = VALID_SPEEDS;
